/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { makeStyles, Theme } from '@material-ui/core/styles';

export const useStyles = makeStyles((theme: Theme) => ({
  content: {
    flexGrow: 1,
    overflow: 'auto',
    ...theme.custom.apiOverviewPage.tabs.tryout.styles
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    maxWidth: '78vw',
  },
  warningContainer: {
    padding: theme.spacing(6.25),
    maxWidth: '57vw',
  },
  swaggerUI: {
    textAlign: 'left',
  },
  swaggerDownloadBtn: {
    ...theme.custom.apiOverviewPage.tabs.tryout.swaggerDownloadButton
  },
  swaggerDownloadBtnGrid: {
    paddingRight: '20px',
  },
  helperText: {
    marginLeft: 0,
    textAlign: 'left',
    lineHeight: theme.spacing(0.2),
    maxWidth: theme.spacing(100)
  },
  tokenType: {
    '& .MuiButton-contained.Mui-disabled span.MuiButton-label': {
      color: '#999999',
    },
  },
  grid: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    paddingRight: theme.spacing(2),
    justifyContent: 'center',
  },
  label: {
    ...theme.custom.apiOverviewPage.tabs.tryout.apiAccessTokenTitleText
  },
  generateKeyButton: {
    width: 'auto',
    marginLeft: theme.spacing(1),
    height: theme.spacing(5),
    ...theme.custom.apiOverviewPage.tabs.tryout.generateKeyButton
  },
  tokenField: {
    width: '70%',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.spacing(0.5),
  },
  testKeyTokenField: {
    border: `${theme.spacing(0.125)}px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.spacing(0.5),
  },
  securityHeaderField: {
    width: '70%',
    backgroundColor: `${theme.palette.common.white}`,
  },
  warningIcon: {
    ...theme.custom.apiOverviewPage.tabs.tryout.warningIcon
  },
  warningText: {
    ...theme.custom.apiOverviewPage.tabs.tryout.warningText,
    display: 'flex',
    alignItems: 'left',
  },
  downloadLink: {
    textDecoration: 'none',
  },
  swaggerLoading: {
    marginLeft: '50%',
  },
  warningLink: {
    ...theme.custom.apiOverviewPage.tabs.tryout.warningLink
  },
  loaderContainer: {
    width: 'cal(100%-60px)',
    height: '70%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    textAlign: 'left',
    fontFamily: 'GilmerMedium',
    fontSize: '13px',
    lineHeight: '14px',
    letterSpacing: 'normal',
    color: '#1D2028',
  },
  componentPadding: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 'auto',
      paddingRight: 'auto',
    },
  },
  textLabel: {
    fontFamily: 'Gilmer',
    fontSize: '13px',
    color: '#1D2028',
    lineHeight: '24px',
    textAlign: 'left',
    ...theme.custom.apiOverviewPage.tabs.tryout.textLabel
  },
  textLabelOptional: {
    ...theme.custom.apiOverviewPage.tabs.tryout.textLabel,
    fontFamily: 'Gilmer',
    fontSize: '10px',
    color: theme.palette.grey[500],
    lineHeight: '15px',
    textAlign: 'left'
  },
  selectBox: {
    height: theme.spacing(5),
    width: '70%',
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent'
    },
    textAlign: 'left',
  },
  dropdownStyle: {
    ...theme.custom.apiOverviewPage.tabs.tryout.applicationDropdownStyle
  },
  goToCredentialsBtn: {
    marginLeft: theme.spacing(3),
    marginTop: -theme.spacing(0.5),
  },
  helpIcon: {
    alignSelf: 'center',
    marginLeft: theme.spacing(1.5),
    ...theme.custom.apiOverviewPage.tabs.tryout.helpIcon
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    ...theme.custom.apiOverviewPage.tabs.tryout.divider
  },
  endpoint: {
    width: '70%',
  },
  selectBg: {
    '& .MuiSelect-select': {
      backgroundColor: '#f7f8fb',
      marginLeft: theme.spacing(0.5),
      textAlign: 'left',
    },
  },
  copyIconButton: {
    position: 'relative',
    marginRight: theme.spacing(1.5),
    ...theme.custom.elements.copyTextField.copyIconButtonStyle,
  },
  keyInfo: {
    backgroundColor: '#F0F1FB',
    borderLeft: 'solid #5567D5',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '70%',
  },
  permissionLabel: {
    width: '30%'
  },
  permissionContent: {
    width: '70%'
  },
  envBox: {
    display: "flex",
    alignItems: "center",
  },
  envValue: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  envChip: {
    minWidth: "100px",
    border: "1px solid #5567d5",
    borderRadius: "15px",
    background: "#5567d5",
    color: "#ffffff",
    height: "25px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    marginRight: theme.spacing(1),
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  envChipSecondary: {
    border: "1px solid #5567d5",
    borderRadius: "15px",
    background: "#f0f1fb",
    color: "#5567d5",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginRight: theme.spacing(1),
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.2,
    padding: "0 8px",
  },
  securityHeaderInput: {
    boxShadow: theme.custom.elements.apiCard.content,
    width: "100%",
    borderRadius: theme.spacing(0.5),
  },
  securityHeaderButton: {
    textTransform: "none",
  },
  securityHeaderLeftBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "30%",
  },
  securityHeaderRightBox: {
    display: "flex",
    flexDirection: "row", 
    alignItems: "flex-start",
    width: "70%"
  },
  securityHeaderContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "80%",
  },
  securityHeaderCard: {
    width: "70%",
  },
  cardActionBox: {
    marginLeft: "auto", 
    display: "flex",
    paddingRight: theme.spacing(1),
  },
  cardActionSpace: {
    width: theme.spacing(1),
  },
  securityHeaderDetailBox: {
    display: "flex",
    alignItems: "center",
    width: "70%",
  },
  SecurityHeaderGridSpace: {
    marginLeft: theme.spacing(0.5),
  },
  securityHeaderButtonBox: {
    display:'block',
    justifyContent:'left',
    alignItems:'flex-start',
    width: '100%',
  },
  envNameStyles: {
    fontWeight: "bold",
    paddingRight: "5px",
  },
  textField: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
    '& .MuiOutlinedInput-input': {
      color: theme.palette.grey[500],
      paddingLeft: theme.spacing(1),
    },
    '& .MuiOutlinedInput-root': {
      minHeight: theme.spacing(4),
      height: 'auto',
    },
  },
  tag: {
    '& .MuiAutocomplete-tag': {
      height: theme.spacing(3),
      borderRadius: theme.spacing(0.625),
      border: "1px solid #E0E2E9",
      backgroundColor: theme.palette.secondary.light,
      "& .MuiChip-label": {
        color: theme.palette.secondary.dark,
      },
      "& .MuiChip-deleteIcon": {
        color: theme.palette.secondary.main,
      },
    },
  },
  autoComplete:{
    width: '70%',
  },
  responseScopeBox:{
    marginLeft: theme.spacing(-0.5),
  }
}));
